import axios from 'axios'
import { store } from '@/store'
import { clearAuth } from '@/features/auth/authSlice'

const BASE_URL = import.meta.env.VITE_API_URL
if (!BASE_URL) {
  throw new Error('[hagatna-admin] VITE_API_URL is not set. Copy .env.example to .env and fill in the value.')
}

const TOKEN_KEY = 'hg_admin_access_token'
const REFRESH_TOKEN_KEY = 'hg_admin_refresh_token'

// ── Token persistence (cross-origin cookie fallback) ────────────────────────
export const getStoredToken = () => localStorage.getItem(TOKEN_KEY)
export const setStoredToken = (token) => {
  if (token) localStorage.setItem(TOKEN_KEY, token)
  else localStorage.removeItem(TOKEN_KEY)
}

export const getStoredRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY)
export const setStoredRefreshToken = (token) => {
  if (token) localStorage.setItem(REFRESH_TOKEN_KEY, token)
  else localStorage.removeItem(REFRESH_TOKEN_KEY)
}

export const clearStoredTokens = () => {
  setStoredToken(null)
  setStoredRefreshToken(null)
}

// ── Axios instance — cookies + Bearer token fallback ────────────────────────
export const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000,
  withCredentials: true,
})

// ── Request interceptor: language + CSRF + Bearer token ─────────────────────
const getCsrfToken = () =>
  document.cookie.split('; ').find((r) => r.startsWith('csrf_token='))?.split('=')[1]

api.interceptors.request.use(
  (config) => {
    const lang = localStorage.getItem('i18nextLng') || 'ar'
    config.headers['Accept-Language'] = lang
    const csrf = getCsrfToken()
    if (csrf) config.headers['x-csrf-token'] = csrf
    const token = getStoredToken()
    if (token) config.headers['Authorization'] = `Bearer ${token}`
    return config
  },
  (error) => Promise.reject(error)
)

// ── Response interceptor: save token + auto-refresh on 401 ──────────────────
let isRefreshing = false
let refreshQueue = []

api.interceptors.response.use(
  (response) => {
    const data = response.data?.data
    if (data?.accessToken) setStoredToken(data.accessToken)
    if (data?.refreshToken) setStoredRefreshToken(data.refreshToken)
    return response
  },
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/login')) {
      if (isRefreshing) {
        return new Promise((resolve) => {
          refreshQueue.push(() => resolve(api(originalRequest)))
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const refreshToken = getStoredRefreshToken()
        const res = await axios.post(
          `${BASE_URL}/auth/refresh`,
          refreshToken ? { refreshToken } : {},
          { withCredentials: true }
        )
        if (res.data?.data?.accessToken) setStoredToken(res.data.data.accessToken)
        refreshQueue.forEach((cb) => cb())
        refreshQueue = []
        return api(originalRequest)
      } catch (refreshError) {
        refreshQueue = []
        clearStoredTokens()
        store.dispatch(clearAuth())
        window.location.href = '/login'
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    // Format validation errors for toast notifications
    if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
      const details = error.response.data.errors.map(err => `• ${err.field || 'Field'}: ${err.message}`).join('\n')
      error.response.data.message = `${error.response.data.message}\n${details}`
    }

    return Promise.reject(error)
  }
)

export default api
